import * as React from 'react';
import { captureContentUpdate } from '../../../../../web/utils/oneDsUtil';
import { URLs } from '../../../../models/constants/constants';
import { sanitizeHTML } from '../../../../utilities';
import { RequestListContainer } from '../../containerComponents/RequestHistory/RequestListContainer';
import { SignInButtonContainer } from '../../containerComponents/SignInButtonContainer';
import { UnAuthIntentGraphContainer } from '../../containerComponents/UnAuthIntentGraphContainer';
import { FreelanceRequest } from '../../models/requestModels';
import {
  IRequestHistoryStyles,
  getRequestHistoryClassNames,
} from '../../styles/requestHistoryStyles';

import { IStyledComponent } from '../../styles/themesUI';
import { SectionHeader } from '../SectionHeader';
import { getString } from '../../../../../web/utils/stringLoader';

interface IRequestHistoryComponentProps {
  isSignedIn: boolean;
  isRequestHistoryEnabled: boolean;
  allRequests: FreelanceRequest[];
  isOrgId: boolean;
  isUserSigningIntoClientApp: boolean;
  isAdmin: boolean;
  isUnifiedPremier: boolean;
  isUnifiedEnterprisePremier: boolean;
  isNonUnifiedPremier: boolean;
  isPremierTenantEnabled: boolean;
  // dispatchers
  dispatchSearchRequests: () => void;
  dispatchSignInUserIntoClientApp: () => void;
}

type IRequestHistoryProps = IRequestHistoryComponentProps & IStyledComponent<IRequestHistoryStyles>;

export class RequestHistory extends React.Component<IRequestHistoryProps> {
  private requestHistoryClassNames = getRequestHistoryClassNames(
    this.props.styles,
    { theme: this.props.theme }
  );

  public componentDidMount = () => {
    this._logRequestHistoryUpdates('componentDidMount', 'RequestHistoryShown');
    if (this.props.isSignedIn && this.props.isRequestHistoryEnabled) {
      this._logRequestHistoryUpdates(
        'componentDidMount',
        'SearchRequestsStarted'
      );
      this.props.dispatchSearchRequests();
    }
  };

  public componentDidUpdate = (prevProps: IRequestHistoryProps) => {
    if (this.props.isUserSigningIntoClientApp) {
      this.props.dispatchSignInUserIntoClientApp();
    }

    if (
      this.props.isSignedIn &&
      !prevProps.isSignedIn &&
      this.props.isRequestHistoryEnabled &&
      !this.props.allRequests
    ) {
      this._logRequestHistoryUpdates(
        'componentDidUpdate',
        'SearchRequestsStarted'
      );
      this.props.dispatchSearchRequests();
    }
  };

  public render = () => {
    // for commercial, if the flight is not turned on we take them to AdminPortal page
    // ToDo: show a similar message for consumer
    if (!this.props.isRequestHistoryEnabled && this.props.isOrgId) {
      return (
        <div
          className={this.requestHistoryClassNames.text}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={this.props.isAdmin ? this._getAdminPortalMessage() : this._getIWMessage()}
        />
      );
    }

    let body = null;
    if (this.props.isSignedIn) {
      body = <RequestListContainer />;
    } else if (this.props.isUserSigningIntoClientApp) {
      return <div className="loader" />;
    } else {
      body = (
        <>
          <SectionHeader
            description={getString('requestHistory_requestSignIn')}
            theme={this.props.theme}
            isUnifiedPremier={this.props.isUnifiedPremier}
            isUnifiedEnterprisePremier={this.props.isUnifiedEnterprisePremier}
            isNonUnifiedPremier={this.props.isNonUnifiedPremier}
            isPremierTenantEnabled={this.props.isPremierTenantEnabled}
          />
          <SignInButtonContainer requestHistorySignin={true} />
          <UnAuthIntentGraphContainer />
        </>
      );
    }

    return (
      <div className={this.requestHistoryClassNames.root}>
        <SectionHeader
          title={getString('requestHistory_mainTitle')}
          theme={this.props.theme}
          isUnifiedPremier={this.props.isUnifiedPremier}
          isUnifiedEnterprisePremier={this.props.isUnifiedEnterprisePremier}
          isNonUnifiedPremier={this.props.isNonUnifiedPremier}
          isPremierTenantEnabled={this.props.isPremierTenantEnabled}
        />
        {body}
      </div>
    );
  };

  private _getAdminPortalMessage = () => {
    this._logRequestHistoryUpdates(
      '_getAdminPortalMessage',
      'AdminPortalSupport'
    );
    const visitAdminPortal = getString('requestHistory_visitAdminPortal');
    const adminPortalMessage = visitAdminPortal ? visitAdminPortal
      .replace(
        '{0}',
        `<a id='adminPortalSupportLink' data-bi-bhvr='SHOW' data-bi-area='RequestHistory' data-bi-formnm='maxFunnel' data-bi-name='adminPortalSupportLink' href="${URLs.AdminPortalRequestHistory}" target="_blank">`
      )
      .replace('{1}', '</a>') : '';
    return sanitizeHTML(adminPortalMessage, ['target']);
  };

  private _getIWMessage = () => {
    this._logRequestHistoryUpdates(
      '_getIWPortalMessage',
      'IWSupport'
    );
    const iwMessage = getString('requestHistory_iwMessage');
    const iwPortalMessage = iwMessage ? iwMessage
      .replace(
        '{0}',
        `<a id="communityForumLink" data-bi-bhvr="COMMUNITY" data-bi-area="RequestHistory" data-bi-name="communityForumLink" data-bi-formnm="maxFunnel" href="${URLs.CommunityLink}" target="_blank">`
      )
      .replace('{1}', '</a>') : '';
    return sanitizeHTML(iwPortalMessage, ['target']);
  };

  private _logRequestHistoryUpdates = (
    methodName: string,
    contentId: string
  ) => {
    const logHandlerTags = {
      content: {
        contentId: contentId,
        contentName: contentId,
        formnm: 'maxFunnel',
        fileName: 'RequestHistory.tsx',
        methodName: methodName,
        areaName: 'RequestHistory',
        actionType: 'KE',
        requestsLength: this.props.allRequests
          ? this.props.allRequests.length
          : 0,
        isSignedIn: this.props.isSignedIn,
        isRequestHistoryEnabled: this.props.isRequestHistoryEnabled,
      },
      behavior: 'SHOW',
    };
    captureContentUpdate(logHandlerTags);
  };
}
